// Pure helpers for the lightweight date-range picker (Community inbox Part D2).
// Everything works in the 'yyyy-mm-dd' ISO date-string space the inbox filters
// already use (dateFrom/dateTo), so there's no timezone drift — a calendar day
// is just its string. Date is used only to lay out the month grid (via UTC so
// the day-of-week is deterministic regardless of the runner's zone).

export interface CalendarDay {
  iso: string; // yyyy-mm-dd
  day: number; // 1..31
  inMonth: boolean; // false = leading/trailing filler from an adjacent month
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;

const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

export function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** {y, m(1-12), d} → 'yyyy-mm-dd'. */
export function toIso(y: number, m: number, d: number): string {
  return `${y}-${pad2(m)}-${pad2(d)}`;
}

/** Parse 'yyyy-mm-dd' → {y, m(1-12), d}; null on anything malformed. */
export function parseIso(iso: string | null | undefined): { y: number; m: number; d: number } | null {
  if (!iso) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return null;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  return { y, m, d };
}

export function daysInMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/** Day-of-week of the 1st (0=Sun … 6=Sat). */
export function firstWeekday(y: number, m: number): number {
  return new Date(Date.UTC(y, m - 1, 1)).getUTCDay();
}

export function monthLabel(y: number, m: number): string {
  return `${MONTHS[m - 1]} ${y}`;
}

/** Move a {y, m} view by `delta` months, wrapping the year. */
export function shiftMonth(y: number, m: number, delta: number): { y: number; m: number } {
  const zeroBased = (m - 1) + delta;
  const ny = y + Math.floor(zeroBased / 12);
  const nm = ((zeroBased % 12) + 12) % 12;
  return { y: ny, m: nm + 1 };
}

/**
 * A 6-week (42-cell) grid for the given month, Sunday-first, padded with the
 * trailing days of the previous month and the leading days of the next so every
 * row is full. `inMonth` flags the filler cells.
 */
export function buildCalendarWeeks(y: number, m: number): CalendarDay[][] {
  const lead = firstWeekday(y, m);
  const total = daysInMonth(y, m);
  const prev = shiftMonth(y, m, -1);
  const prevTotal = daysInMonth(prev.y, prev.m);
  const next = shiftMonth(y, m, 1);

  const cells: CalendarDay[] = [];
  // Leading filler (previous month's tail).
  for (let i = lead - 1; i >= 0; i--) {
    const d = prevTotal - i;
    cells.push({ iso: toIso(prev.y, prev.m, d), day: d, inMonth: false });
  }
  // This month.
  for (let d = 1; d <= total; d++) {
    cells.push({ iso: toIso(y, m, d), day: d, inMonth: true });
  }
  // Trailing filler (next month's head) up to 42 cells.
  let d = 1;
  while (cells.length < 42) {
    cells.push({ iso: toIso(next.y, next.m, d), day: d, inMonth: false });
    d++;
  }

  const weeks: CalendarDay[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

/** Inclusive [from, to] membership; open-ended when a bound is null. */
export function isWithinRange(
  iso: string,
  from: string | null,
  to: string | null,
): boolean {
  if (from && iso < from) return false;
  if (to && iso > to) return false;
  return from !== null || to !== null;
}

function fmtShort(iso: string): string {
  const p = parseIso(iso);
  if (!p) return iso;
  return `${p.d} ${MONTHS_SHORT[p.m - 1]} ${p.y}`;
}

/** Trigger-button label for a (possibly partial / empty) range. */
export function formatRangeLabel(
  from: string | null | undefined,
  to: string | null | undefined,
): string {
  const f = from || null;
  const t = to || null;
  if (!f && !t) return 'Any dates';
  if (f && t) return f === t ? fmtShort(f) : `${fmtShort(f)} – ${fmtShort(t)}`;
  if (f) return `From ${fmtShort(f)}`;
  return `Until ${fmtShort(t!)}`;
}

/**
 * Fold a click on `iso` into the current [from, to] selection with anchor-then-
 * extend semantics: with no complete range (nothing, or a full range already
 * set) a click starts a fresh single-day range; with exactly a start set, a
 * click sets the end (auto-swapping if the click precedes the start).
 */
export function applyDayClick(
  iso: string,
  from: string | null,
  to: string | null,
): { from: string; to: string | null } {
  // No open selection (empty, or already a complete range) → start over.
  if (!from || to) return { from: iso, to: null };
  // Extend: second click completes the range (swap if needed).
  return iso < from ? { from: iso, to: from } : { from, to: iso };
}
